<script setup lang="ts">
import { NO_KEY_KPI } from '@zerohuman/shared'
import { Button, ZhEmptyState, ZhKpiLight, ZhPageTitle, ZhPanel, ZhSystemLabel, ZhTooltip } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, type KpiBoard, type KpiRag, type Team } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'

const router = useRouter()
const teams = ref<Team[]>([])
const error = ref<string | null>(null)
const deleting = ref<string | null>(null)
const loading = ref(true)
/** `null` until the board is read, and it stays `null` when that read fails: unknown is not "none set". */
const kpiBoard = ref<KpiBoard | null>(null)

/**
 * A team row with its key KPI drawn out flat (#898), so the cell reads plain fields: the light's colour
 * (`null` draws no light), the KPI's name, and the note that stands in its place — "No key KPI set" when
 * the board says the team has none, an em dash while the board is unread.
 */
type TeamRow = Team & { kpiRag: KpiRag | null; kpiName: string; kpiNote: string }

const teamRows = computed<TeamRow[]>(() =>
    teams.value.map(team => {
        const row = kpiBoard.value?.teams.find(entry => entry.teamId === team.id)
        return {
            ...team,
            kpiRag: row?.key?.rag ?? null,
            kpiName: row?.key?.name ?? '',
            kpiNote: row && !row.key ? NO_KEY_KPI : '—',
        }
    }),
)

/** Teams are one page of rows the tenant owns: search, sort and page them in the browser. */
const table = useClientTable({
    data: teamRows,
    searchFields: ['name', 'slug', 'charter.purpose', 'charter.metric', 'leadName'],
    defaultSort: 'name',
    defaultOrder: 'ASC',
})
/** #633: the table's search, sort and filters come back on the next visit. */
const list = useListState('teams', table.listFields)

const columns: DataTableColumn[] = [
    { key: 'name', label: 'Team', sort: 'name', type: 'text' },
    { key: 'slug', label: 'Slug', sort: 'slug', type: 'text' },
    { key: 'charter.purpose', label: 'Goal', filterable: false, class: 'max-w-sm' },
    // The light the CEO scans the list by, and the same one HQ shows for that team (#898). It replaces the
    // one-metric column outright — a sentence with no target, no light and no reading date was never
    // scannable, and the charter still holds it, edited on the team's own page.
    { key: 'keyKpi', label: 'Key KPI', filterable: false },
    { key: 'leadName', label: 'Lead', filterable: false },
    {
        key: 'status',
        label: 'Status',
        sort: 'status',
        type: 'select',
        options: [
            { value: 'active', label: 'active' },
            { value: 'archived', label: 'archived' },
        ],
        defaultVisible: false,
    },
    { key: 'actions', label: 'Actions', filterable: false, class: 'text-right' },
]

onMounted(async () => {
    await load()
})

async function load(): Promise<void> {
    loading.value = true
    try {
        teams.value = await api.teams()
        error.value = null
    } catch {
        error.value = 'Could not load teams.'
    } finally {
        loading.value = false
    }
    // The lights load beside the teams, never instead of them: a KPI read that fails leaves the list standing,
    // with the Key KPI column reading "—" rather than claiming every team has none set.
    try {
        kpiBoard.value = await api.kpiBoard()
    } catch {
        kpiBoard.value = null
    }
}

async function remove(team: Team): Promise<void> {
    deleting.value = team.id
    try {
        await api.deleteTeam(team.id)
        await load()
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        error.value = code === 'last_team' ? 'Cannot delete the last team.' : 'Could not delete team.'
    } finally {
        deleting.value = null
    }
}

function open(row: Record<string, unknown>): void {
    void router.push(`/teams/${String(row.id)}`)
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex items-center justify-between gap-4">
			<ZhPageTitle>Teams</ZhPageTitle>
			<Button as="a" href="/teams/new" data-testid="create-button" @click.prevent="router.push('/teams/new')">
				Create
			</Button>
		</div>
		<p class="text-muted-foreground text-sm">
			Every team carries a goal, its KPIs, and the lead who answers for it. Click a row to open the team.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<!-- The teams, on a kit surface of their own; the table draws its own cell padding, so the panel is flush. -->
		<ZhPanel flush>
			<DataTable
				storage-key="teams"
				:columns="columns"
				:loading="loading"
				:list-state="list"
				v-bind="table.tableProps.value"
				@row-click="open"
			>
				<template #cell-name="{ row }">
					<span class="flex items-center gap-2">
						<RouterLink class="font-medium underline-offset-2 hover:underline" :to="`/teams/${row.id}`">
							{{ row.name }}
						</RouterLink>
						<!-- A team switched off (#1811) says so beside its name. Omitted reads as on, so it draws nothing. -->
						<ZhSystemLabel
							v-if="row.enabled === false"
							as="span"
							tone="muted"
							:data-testid="`team-off-${row.id}`"
						>
							Off
						</ZhSystemLabel>
					</span>
				</template>
				<template #cell-keyKpi="{ row }">
					<span :data-testid="`team-kpi-${row.id}`" class="flex items-center gap-2">
						<ZhKpiLight v-if="row.kpiRag" :rag="(row.kpiRag as KpiRag)" />
						<span v-if="row.kpiName">{{ row.kpiName }}</span>
						<span v-else class="text-muted-foreground">{{ row.kpiNote }}</span>
					</span>
				</template>
				<template #cell-leadName="{ row }">
					<!-- The column header already says Lead, so the chip was labelling itself. A name is a name. -->
					<span v-if="row.leadName">{{ row.leadName }}</span>
					<!-- Still a chip: "COO acting as lead" is a standing-in state, not a person's name. -->
					<ZhSystemLabel v-else-if="row.cooActingAsLead" as="span" tone="system">COO acting as lead</ZhSystemLabel>
					<span v-else class="text-muted-foreground">—</span>
				</template>
				<template #cell-actions="{ row }">
					<ZhTooltip :text="teams.length <= 1 ? 'Cannot delete the last team.' : 'Delete team'"><Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="teams.length <= 1 || deleting === row.id"
						@click.stop="remove(row as unknown as Team)"
					>
						Delete
					</Button></ZhTooltip>
				</template>
			</DataTable>
		</ZhPanel>
		<ZhEmptyState v-if="!loading && !teams.length && !error" label="No teams">
			No teams yet. Teams you create appear here, with their members and KPIs.
			<template #action>
				<Button as-child size="sm"><RouterLink to="/teams/new">Create a team</RouterLink></Button>
			</template>
		</ZhEmptyState>
	</section>
</template>
